import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../App';
import toast from 'react-hot-toast';

/* ── Credit Score Gauge ────────────────────────────────── */
function CreditGauge({ score, grade, gradeColor, confidence, range }) {
  const pct = Math.max(0, Math.min(100, ((score - 300) / 550) * 100));
  return (
    <div data-testid="credit-gauge" className="relative flex flex-col items-center">
      <svg width="140" height="80" viewBox="0 0 140 80">
        <path d="M15 75 A55 55 0 0 1 125 75" fill="none" stroke="#1e1e2e" strokeWidth="10" strokeLinecap="round" />
        <path d="M15 75 A55 55 0 0 1 125 75" fill="none" stroke="url(#scoreGrad)" strokeWidth="10" strokeLinecap="round"
          strokeDasharray={`${pct * 1.72} 172`} className="transition-all duration-1000" />
        <defs>
          <linearGradient id="scoreGrad" x1="0" y1="0" x2="1" y2="0">
            <stop offset="0%" stopColor="#ef4444" /><stop offset="35%" stopColor="#f59e0b" />
            <stop offset="65%" stopColor="#22c55e" /><stop offset="100%" stopColor="#10b981" />
          </linearGradient>
        </defs>
      </svg>
      <div className="absolute top-7 text-center">
        <div className="text-3xl font-black text-white tabular-nums">{score}</div>
        <div className="text-[10px] font-bold uppercase tracking-wider" style={{ color: gradeColor }}>{grade}</div>
      </div>
      <div className="mt-1 flex items-center gap-2 text-[10px] text-zinc-500">
        <span>{range?.[0] || 300}–{range?.[1] || 850}</span>
        <span className="px-1.5 py-0.5 rounded bg-white/5 border border-white/10">{confidence}% conf</span>
      </div>
    </div>
  );
}

/* ── Phone Result Card ─────────────────────────────────── */
function PhoneCard({ phone, index }) {
  const conf = phone.confidence || 0;
  const color = conf >= 80 ? '#10b981' : conf >= 50 ? '#f59e0b' : conf >= 25 ? '#f97316' : '#ef4444';
  return (
    <div data-testid={`phone-result-${index}`} className="flex items-center justify-between p-3 rounded-lg bg-white/[0.02] border border-white/[0.06] hover:border-white/10 transition-all group">
      <div className="flex items-center gap-3">
        <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ background: `${color}12` }}>
          <i className="fa-solid fa-phone text-xs" style={{ color }} />
        </div>
        <div>
          <div className="text-sm font-bold text-white font-mono tracking-wide">{phone.phone}</div>
          <div className="text-[10px] text-zinc-500 flex items-center gap-1.5 mt-0.5">
            {phone.source}
            {phone.carrier && <span className="text-zinc-400">| {phone.carrier}</span>}
          </div>
        </div>
      </div>
      <div className="flex items-center gap-2.5">
        {phone.twilio_verified && (
          <span className="text-[9px] px-1.5 py-0.5 rounded bg-violet-500/15 text-violet-400 border border-violet-500/20 font-bold">TWILIO VERIFIED</span>
        )}
        {phone.numverify_valid && (
          <span className="text-[9px] px-1.5 py-0.5 rounded bg-cyan-500/15 text-cyan-400 border border-cyan-500/20 font-bold">NUMVERIFY</span>
        )}
        {phone.twilio_line_type && (
          <span className="text-[9px] px-1.5 py-0.5 rounded bg-white/5 text-zinc-400 border border-white/10">{phone.twilio_line_type}</span>
        )}
        <div className="text-right min-w-[56px]">
          <div className="text-sm font-black tabular-nums" style={{ color }}>{conf}%</div>
          <div className="w-14 h-1.5 rounded-full bg-white/10 overflow-hidden">
            <div className="h-full rounded-full transition-all duration-700" style={{ width: `${conf}%`, background: `linear-gradient(90deg, ${color}88, ${color})` }} />
          </div>
        </div>
      </div>
    </div>
  );
}

/* ── Wealth Indicator Card ────────────────────────────── */
function WealthCard({ label, value, sub, icon, color }) {
  return (
    <div className="p-3 rounded-lg bg-white/[0.02] border border-white/[0.06]">
      <div className="flex items-center gap-2 mb-1.5">
        <i className={`fa-solid ${icon} text-[10px]`} style={{ color }} />
        <span className="text-[10px] font-bold uppercase tracking-wider text-zinc-500">{label}</span>
      </div>
      <div className="text-sm font-bold text-white">{value}</div>
      {sub && <div className="text-[10px] text-zinc-500 mt-0.5">{sub}</div>}
    </div>
  );
}

/* ── Credit Score Factor ──────────────────────────────── */
function ScoreFactor({ factor }) {
  const signalColor = factor.signal === 'positive' ? '#10b981' : factor.signal === 'neutral' ? '#f59e0b' : '#ef4444';
  return (
    <div className="flex items-center gap-2 py-1.5">
      <div className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ background: signalColor }} />
      <span className="text-xs text-zinc-300 flex-1">{factor.factor}</span>
      <span className="text-xs font-mono font-bold" style={{ color: signalColor }}>{factor.impact}</span>
    </div>
  );
}

/* ════════════════════════════════════════════════════════ */
export default function SkipTrace() {
  const [leads, setLeads] = useState([]);
  const [tracing, setTracing] = useState({});
  const [results, setResults] = useState({});
  const [pricing, setPricing] = useState(null);
  const [history, setHistory] = useState([]);
  const [batchMode, setBatchMode] = useState(false);
  const [selectedLeads, setSelectedLeads] = useState(new Set());
  const [batchLoading, setBatchLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [tab, setTab] = useState('trace');
  const [expandedResult, setExpandedResult] = useState(null);

  const fetchLeads = useCallback(async () => {
    try { const res = await api.get('/api/leads?limit=100'); setLeads(res.data.leads || []); } catch {}
  }, []);
  const fetchPricing = useCallback(async () => {
    try { const res = await api.get('/api/skip-trace/pricing'); setPricing(res.data); } catch {}
  }, []);
  const fetchHistory = useCallback(async () => {
    try { const res = await api.get('/api/skip-trace/history'); setHistory(res.data.records || []); } catch {}
  }, []);

  useEffect(() => { fetchLeads(); fetchPricing(); fetchHistory(); }, [fetchLeads, fetchPricing, fetchHistory]);

  const runSkipTrace = async (lead) => {
    const lid = lead.id || lead._id;
    setTracing(prev => ({ ...prev, [lid]: true }));
    try {
      const res = await api.post('/api/skip-trace/single', {
        lead_id: lid, owner_name: lead.owner_name || lead.name,
        zip_code: lead.zip, address: lead.address, state: lead.state,
        city: lead.city, mailing_address: lead.mailing_address,
      });
      setResults(prev => ({ ...prev, [lid]: res.data }));
      setExpandedResult(lid);
      const cached = res.data.from_cache;
      const phone = res.data.skip_trace?.best_phone || 'No phone';
      toast.success(cached ? `From DB Cache (FREE): ${phone}` : `Skip Trace complete: ${phone}`);
      fetchHistory(); fetchPricing();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Skip trace failed');
    }
    setTracing(prev => ({ ...prev, [lid]: false }));
  };

  const runBatchTrace = async () => {
    if (selectedLeads.size === 0) return toast.error('Select leads first');
    setBatchLoading(true);
    try {
      const res = await api.post('/api/skip-trace/batch', { lead_ids: [...selectedLeads] });
      toast.success(`Batch: ${res.data.total_traced}/${res.data.total_requested} traced`);
      const newResults = {};
      for (const r of res.data.results || []) {
        if (r.success) newResults[r.lead_id] = { skip_trace: r.skip_trace, cost: res.data.price_per_trace };
      }
      setResults(prev => ({ ...prev, ...newResults }));
      setSelectedLeads(new Set()); setBatchMode(false);
      fetchHistory(); fetchPricing();
    } catch (err) { toast.error(err.response?.data?.detail || 'Batch trace failed'); }
    setBatchLoading(false);
  };

  const exportLeads = async () => {
    setExporting(true);
    try {
      const res = await api.get('/api/export/leads', { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url; link.setAttribute('download', `solar_leads_${new Date().toISOString().slice(0, 10)}.csv`);
      document.body.appendChild(link); link.click(); link.remove(); window.URL.revokeObjectURL(url);
      toast.success('Export downloaded!');
    } catch { toast.error('Export failed'); }
    setExporting(false);
  };

  const toggleSelect = (id) => setSelectedLeads(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const selectAll = () => selectedLeads.size === leads.length ? setSelectedLeads(new Set()) : setSelectedLeads(new Set(leads.map(l => l.id || l._id)));

  const dbStats = pricing?.db_stats || {};

  return (
    <div data-testid="skip-trace-page" className="space-y-6 pb-12">
      {/* ── HERO ─────────────────────────────────────────── */}
      <div className="relative overflow-hidden rounded-2xl border border-white/[0.06] p-8"
        style={{ background: 'linear-gradient(135deg, #0c0a1a 0%, #0f1225 40%, #0a1a15 100%)' }}>
        <div className="absolute inset-0 opacity-40" style={{ background: 'radial-gradient(ellipse at 15% 50%, rgba(139,92,246,0.15) 0%, transparent 60%), radial-gradient(ellipse at 85% 30%, rgba(16,185,129,0.1) 0%, transparent 60%)' }} />
        <div className="relative z-10 flex flex-col lg:flex-row items-start justify-between gap-6">
          <div className="flex-1">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-violet-600 to-emerald-500 flex items-center justify-center shadow-lg shadow-violet-500/20">
                <i className="fa-solid fa-user-secret text-white text-xl" />
              </div>
              <div>
                <h1 className="text-2xl font-black text-white tracking-tight">Skip Trace Intelligence</h1>
                <p className="text-sm text-zinc-400">In-house database-powered contact discovery & credit analysis</p>
              </div>
            </div>
            <p className="text-xs text-zinc-500 max-w-xl leading-relaxed">
              Proprietary skip trace engine with OSINT phone discovery, NumVerify + Twilio verification,
              FICO credit estimation, and wealth analysis. Cached results = free repeat lookups.
            </p>
          </div>

          {/* DB Stats + Pricing */}
          <div className="flex gap-4">
            <div className="p-4 rounded-xl bg-white/[0.03] border border-white/[0.08] min-w-[130px]">
              <div className="text-[10px] font-bold uppercase tracking-wider text-emerald-400 mb-2">In-House DB</div>
              <div className="text-2xl font-black text-white tabular-nums">{dbStats.cache_size || 0}</div>
              <div className="text-[10px] text-zinc-500">records cached</div>
              <div className="text-xs text-emerald-400 font-bold mt-1">${dbStats.savings_from_cache?.toFixed(2) || '0.00'} saved</div>
            </div>
            {pricing && (
              <div className="p-4 rounded-xl bg-white/[0.03] border border-white/[0.08] min-w-[130px]">
                <div className="text-[10px] font-bold uppercase tracking-wider text-violet-400 mb-2">Pricing</div>
                <div className="text-2xl font-black text-white">${pricing.single_price?.toFixed(2)}</div>
                <div className="text-[10px] text-zinc-500">per trace</div>
                <div className="text-xs text-violet-400 mt-1">{pricing.batch_discount} off for 10+</div>
                <div className="text-sm text-amber-400 mt-1 font-bold">{pricing.user_credits?.toFixed(0)} credits</div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ── TABS ─────────────────────────────────────────── */}
      <div className="flex items-center gap-2 flex-wrap">
        {[
          { id: 'trace', label: 'Skip Trace', icon: 'fa-user-secret' },
          { id: 'history', label: 'History', icon: 'fa-clock-rotate-left' },
          { id: 'tiers', label: 'Client Tiers', icon: 'fa-layer-group' },
        ].map(t => (
          <button key={t.id} data-testid={`tab-${t.id}`} onClick={() => setTab(t.id)}
            className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all flex items-center gap-2 ${
              tab === t.id ? 'bg-violet-500/15 text-violet-400 border border-violet-500/30' : 'text-zinc-500 hover:text-zinc-300 border border-transparent hover:border-white/10'}`}>
            <i className={`fa-solid ${t.icon} text-xs`} /> {t.label}
          </button>
        ))}
        <div className="flex-1" />
        <button data-testid="export-csv-btn" onClick={exportLeads} disabled={exporting}
          className="px-4 py-2 rounded-lg text-sm font-semibold text-emerald-400 bg-emerald-500/10 border border-emerald-500/20 hover:bg-emerald-500/20 transition-all flex items-center gap-2 disabled:opacity-50">
          <i className={`fa-solid ${exporting ? 'fa-spinner animate-spin' : 'fa-file-csv'} text-xs`} /> Export CSV
        </button>
      </div>

      {/* ── TRACE TAB ────────────────────────────────────── */}
      {tab === 'trace' && (
        <div className="space-y-4">
          {/* Batch Controls */}
          <div className="flex items-center gap-3 flex-wrap">
            <button data-testid="toggle-batch-mode" onClick={() => { setBatchMode(!batchMode); setSelectedLeads(new Set()); }}
              className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${batchMode ? 'bg-violet-500/15 text-violet-400 border border-violet-500/30' : 'text-zinc-500 bg-white/5 border border-white/10 hover:text-zinc-300'}`}>
              <i className="fa-solid fa-layer-group mr-1.5" /> {batchMode ? 'Cancel Batch' : 'Batch Mode'}
            </button>
            {batchMode && (
              <>
                <button data-testid="select-all-btn" onClick={selectAll} className="text-xs text-zinc-400 hover:text-white transition-colors">
                  {selectedLeads.size === leads.length ? 'Deselect All' : 'Select All'} ({selectedLeads.size}/{leads.length})
                </button>
                <button data-testid="run-batch-trace-btn" onClick={runBatchTrace} disabled={batchLoading || selectedLeads.size === 0}
                  className="px-4 py-1.5 rounded-lg text-xs font-bold bg-gradient-to-r from-violet-600 to-emerald-600 text-white disabled:opacity-50 flex items-center gap-2 shadow-lg shadow-violet-500/10">
                  {batchLoading ? <i className="fa-solid fa-spinner animate-spin" /> : <i className="fa-solid fa-bolt" />}
                  Trace {selectedLeads.size} Leads
                </button>
              </>
            )}
          </div>

          {leads.length === 0 && (
            <div className="text-center py-20 opacity-40">
              <i className="fa-solid fa-users text-5xl text-zinc-600 mb-3" />
              <p className="text-zinc-500 text-sm">No leads found. Scan or import leads first.</p>
            </div>
          )}

          {/* Lead Cards */}
          <div className="space-y-3">
            {leads.map((lead, i) => {
              const lid = lead.id || lead._id;
              const traceData = results[lid] || {};
              const traceResult = traceData.skip_trace || lead.skip_trace;
              const isTracing = tracing[lid];
              const isSelected = selectedLeads.has(lid);
              const isExpanded = expandedResult === lid && traceResult;
              const fromCache = traceData.from_cache || traceResult?.from_cache;
              const hasCredit = traceResult?.credit_score;
              const hasRealPhone = traceResult?.best_phone && !traceResult.best_phone.includes('---');

              return (
                <div key={lid} data-testid={`skip-trace-lead-${i}`}
                  className={`rounded-xl border overflow-hidden transition-all ${
                    isSelected ? 'border-violet-500/40 bg-violet-500/[0.03]' : 'border-white/[0.06] bg-white/[0.015]'
                  }`}>
                  {/* Lead Header */}
                  <div className="p-4 flex items-center gap-4">
                    {batchMode && (
                      <button onClick={() => toggleSelect(lid)}
                        className={`w-5 h-5 rounded border flex items-center justify-center transition-all flex-shrink-0 ${
                          isSelected ? 'bg-violet-500 border-violet-500' : 'border-zinc-600 hover:border-zinc-400'}`}>
                        {isSelected && <i className="fa-solid fa-check text-white text-[10px]" />}
                      </button>
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="text-sm font-bold text-white truncate">{lead.owner_name || lead.name}</h3>
                        {lead.skip_trace && (
                          <span className="text-[9px] px-1.5 py-0.5 rounded bg-violet-500/15 text-violet-400 border border-violet-500/20 font-bold">TRACED</span>
                        )}
                        {fromCache && (
                          <span className="text-[9px] px-1.5 py-0.5 rounded bg-emerald-500/15 text-emerald-400 border border-emerald-500/20 font-bold">FROM DB</span>
                        )}
                      </div>
                      <p className="text-xs text-zinc-500 truncate">{lead.address}, {lead.city}, {lead.state} {lead.zip}</p>
                    </div>
                    <div className="hidden sm:flex items-center gap-4 text-xs text-zinc-500 flex-shrink-0">
                      <span>${(lead.estimated_value || lead.property_value || 0).toLocaleString()}</span>
                      <span className={`font-semibold ${lead.solar_potential === 'High' ? 'text-emerald-400' : 'text-amber-400'}`}>{lead.solar_potential || '—'}</span>
                      <span className="font-mono">{lead.score || '—'}</span>
                    </div>
                    {!batchMode && (
                      <div className="flex items-center gap-2 flex-shrink-0">
                        {traceResult && (
                          <button data-testid={`expand-result-${i}`}
                            onClick={() => setExpandedResult(isExpanded ? null : lid)}
                            className="px-3 py-2 rounded-lg text-xs font-semibold text-zinc-400 bg-white/5 border border-white/10 hover:text-white transition-all">
                            <i className={`fa-solid ${isExpanded ? 'fa-chevron-up' : 'fa-chevron-down'}`} />
                          </button>
                        )}
                        <button data-testid={`trace-btn-${i}`} onClick={() => runSkipTrace(lead)} disabled={isTracing}
                          className="px-4 py-2 rounded-lg text-xs font-bold bg-gradient-to-r from-violet-600 to-emerald-600 text-white hover:shadow-lg hover:shadow-violet-500/20 disabled:opacity-50 transition-all flex items-center gap-2 whitespace-nowrap">
                          {isTracing ? <><i className="fa-solid fa-spinner animate-spin" /> Tracing...</> : <><i className="fa-solid fa-user-secret" /> Skip Trace</>}
                        </button>
                      </div>
                    )}
                  </div>

                  {/* ── Expanded Result Panel ─────────────── */}
                  {isExpanded && traceResult && (
                    <div className="border-t border-white/[0.06] bg-white/[0.01]">
                      {/* Summary Bar */}
                      <div className="px-4 py-3 flex items-center justify-between flex-wrap gap-2">
                        <div className="flex items-center gap-3">
                          <span className="text-[10px] text-zinc-500 uppercase tracking-wider font-bold">Skip Trace Result</span>
                          {fromCache && <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 font-bold">CACHED (FREE)</span>}
                        </div>
                        <div className="flex items-center gap-3 text-xs">
                          <span className="text-zinc-500">{traceResult.duration_ms}ms</span>
                          <span className={`font-black ${traceResult.accuracy_score >= 70 ? 'text-emerald-400' : traceResult.accuracy_score >= 40 ? 'text-amber-400' : 'text-red-400'}`}>
                            {traceResult.accuracy_score}% accuracy
                          </span>
                        </div>
                      </div>

                      <div className="px-4 pb-5 grid lg:grid-cols-3 gap-4">
                        {/* ── Column 1: Phones ─── */}
                        <div className="space-y-2">
                          <div className="text-[10px] font-bold uppercase tracking-wider text-zinc-500 mb-2">
                            <i className="fa-solid fa-phone mr-1 text-violet-400" /> Phone Discovery ({traceResult.phones?.length || 0})
                          </div>
                          {traceResult.phones?.map((p, j) => <PhoneCard key={j} phone={p} index={j} />)}
                          {/* Twilio Intel */}
                          {hasRealPhone && traceResult.twilio_result && !traceResult.twilio_result.error && (
                            <div className="p-3 rounded-lg bg-violet-500/[0.06] border border-violet-500/15 mt-2">
                              <div className="text-[10px] font-bold text-violet-400 uppercase tracking-wider mb-2">
                                <i className="fa-solid fa-crown mr-1" /> Twilio Premium Intel
                              </div>
                              <div className="grid grid-cols-2 gap-2 text-xs">
                                <div className="text-zinc-500">Valid: <span className={traceResult.twilio_result.valid ? 'text-emerald-400 font-bold' : 'text-red-400 font-bold'}>{traceResult.twilio_result.valid ? 'YES' : 'NO'}</span></div>
                                <div className="text-zinc-500">Carrier: <span className="text-white">{traceResult.twilio_result.carrier_name || '—'}</span></div>
                                <div className="text-zinc-500">Type: <span className="text-white">{traceResult.twilio_result.line_type || '—'}</span></div>
                                <div className="text-zinc-500">Caller: <span className="text-white">{traceResult.twilio_result.caller_name || '—'}</span></div>
                              </div>
                            </div>
                          )}
                        </div>

                        {/* ── Column 2: Credit Score ─── */}
                        <div className="space-y-3">
                          <div className="text-[10px] font-bold uppercase tracking-wider text-zinc-500 mb-2">
                            <i className="fa-solid fa-chart-line mr-1 text-emerald-400" /> Credit Score Estimation
                          </div>
                          {hasCredit ? (
                            <div className="p-4 rounded-xl bg-white/[0.02] border border-white/[0.06]">
                              <CreditGauge
                                score={traceResult.credit_score.estimated_score}
                                grade={traceResult.credit_score.grade}
                                gradeColor={traceResult.credit_score.grade_color}
                                confidence={traceResult.credit_score.confidence}
                                range={traceResult.credit_score.score_range}
                              />
                              <div className="mt-3 border-t border-white/5 pt-2">
                                <div className="text-[10px] font-bold text-zinc-500 uppercase mb-1">Score Factors</div>
                                {traceResult.credit_score.factors?.map((f, j) => <ScoreFactor key={j} factor={f} />)}
                              </div>
                              <div className="mt-2 text-[9px] text-zinc-600 italic">{traceResult.credit_score.disclaimer}</div>
                            </div>
                          ) : (
                            <div className="p-6 rounded-xl bg-white/[0.02] border border-white/[0.06] text-center text-zinc-600 text-xs">
                              No property data for credit estimation
                            </div>
                          )}
                        </div>

                        {/* ── Column 3: Wealth Indicators ─── */}
                        <div className="space-y-3">
                          <div className="text-[10px] font-bold uppercase tracking-wider text-zinc-500 mb-2">
                            <i className="fa-solid fa-gem mr-1 text-amber-400" /> Wealth Indicators
                          </div>
                          {traceResult.wealth_indicators ? (
                            <div className="grid grid-cols-2 gap-2">
                              <WealthCard label="Net Worth" icon="fa-ranking-star" color="#f59e0b"
                                value={traceResult.wealth_indicators.net_worth_indicator || '—'}
                                sub="Property-based estimate" />
                              <WealthCard label="Home Equity" icon="fa-house-chimney" color="#10b981"
                                value={traceResult.wealth_indicators.estimated_home_equity ? `$${Number(traceResult.wealth_indicators.estimated_home_equity).toLocaleString()}` : '—'}
                                sub={traceResult.wealth_indicators.equity_percentage ? `${traceResult.wealth_indicators.equity_percentage}% equity` : ''} />
                              <WealthCard label="Market Value" icon="fa-money-bill-trend-up" color="#3b82f6"
                                value={traceResult.wealth_indicators.market_value ? `$${Number(traceResult.wealth_indicators.market_value).toLocaleString()}` : '—'} />
                              <WealthCard label="Annual Tax" icon="fa-file-invoice-dollar" color="#8b5cf6"
                                value={traceResult.wealth_indicators.tax_amount ? `$${Number(traceResult.wealth_indicators.tax_amount).toLocaleString()}` : '—'} />
                              <WealthCard label="Ownership" icon="fa-calendar-check" color="#06b6d4"
                                value={traceResult.wealth_indicators.ownership_years ? `${traceResult.wealth_indicators.ownership_years} years` : '—'} />
                              <WealthCard label="Investor" icon="fa-building" color="#ec4899"
                                value={traceResult.wealth_indicators.is_investor ? 'Yes (Absentee)' : 'No'}
                                sub="Absentee owner check" />
                            </div>
                          ) : (
                            <div className="p-6 rounded-xl bg-white/[0.02] border border-white/[0.06] text-center text-zinc-600 text-xs">
                              No wealth data available
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Sources */}
                      <div className="px-4 pb-4">
                        <div className="flex flex-wrap gap-1">
                          {traceResult.sources_used?.map((s, j) => (
                            <span key={j} className="text-[9px] px-2 py-0.5 rounded-full bg-white/[0.03] text-zinc-500 border border-white/[0.06]">{s}</span>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ── HISTORY TAB ──────────────────────────────────── */}
      {tab === 'history' && (
        <div className="space-y-3">
          {history.length === 0 ? (
            <div className="text-center py-20 opacity-40">
              <i className="fa-solid fa-clock-rotate-left text-5xl text-zinc-600 mb-3" />
              <p className="text-zinc-500 text-sm">No skip trace history yet</p>
            </div>
          ) : history.map((rec, i) => {
            const st = rec.result || {};
            return (
              <div key={i} data-testid={`history-item-${i}`} className="rounded-xl border border-white/[0.06] bg-white/[0.015] p-4">
                <div className="flex items-center justify-between gap-4">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="w-9 h-9 rounded-lg flex items-center justify-center bg-violet-500/10 flex-shrink-0">
                      <i className="fa-solid fa-user-secret text-violet-400 text-xs" />
                    </div>
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <h3 className="text-sm font-bold text-white truncate">{rec.owner_name}</h3>
                        {rec.from_cache && <span className="text-[9px] px-1.5 py-0.5 rounded bg-emerald-500/15 text-emerald-400 border border-emerald-500/20 font-bold">CACHED</span>}
                      </div>
                      <p className="text-xs text-zinc-500 truncate">{rec.address} | {new Date(rec.created_at).toLocaleDateString()}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4 flex-shrink-0">
                    <div className="text-right">
                      <div className="text-sm font-mono text-white font-bold">{st.best_phone || '—'}</div>
                      <div className="text-[10px] text-zinc-500">{st.accuracy_score}% accuracy</div>
                    </div>
                    {st.credit_score && (
                      <div className="text-right hidden sm:block">
                        <div className="text-sm font-bold" style={{ color: st.credit_score.grade_color }}>{st.credit_score.estimated_score}</div>
                        <div className="text-[10px] text-zinc-500">{st.credit_score.grade}</div>
                      </div>
                    )}
                    <div className="text-right">
                      <div className={`text-sm font-bold ${rec.cost === 0 ? 'text-emerald-400' : 'text-white'}`}>
                        {rec.cost === 0 ? 'FREE' : `$${rec.cost?.toFixed(2)}`}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* ── CLIENT TIERS TAB ─────────────────────────────── */}
      {tab === 'tiers' && pricing?.client_tiers && (
        <div className="space-y-4">
          <div className="text-sm text-zinc-400 mb-2">
            Upcharge your clients using these tiers. Your internal cost is <span className="text-violet-400 font-bold">${pricing.single_price}/trace</span>.
          </div>
          <div className="grid md:grid-cols-3 gap-4">
            {Object.entries(pricing.client_tiers).map(([tier, info]) => {
              const colors = { basic: { bg: '#3b82f620', border: '#3b82f640', text: '#3b82f6' }, professional: { bg: '#8b5cf620', border: '#8b5cf640', text: '#8b5cf6' }, enterprise: { bg: '#f59e0b20', border: '#f59e0b40', text: '#f59e0b' } };
              const c = colors[tier] || colors.basic;
              const profit = info.price_per_trace - pricing.single_price;
              return (
                <div key={tier} data-testid={`tier-${tier}`}
                  className="rounded-xl border p-6 transition-all hover:scale-[1.02]"
                  style={{ borderColor: c.border, background: c.bg }}>
                  <div className="text-[10px] font-bold uppercase tracking-widest mb-3" style={{ color: c.text }}>{tier}</div>
                  <div className="text-3xl font-black text-white mb-1">${info.price_per_trace.toFixed(2)}</div>
                  <div className="text-xs text-zinc-500 mb-4">per trace</div>
                  <div className="text-xs font-bold text-emerald-400 mb-3">
                    +${profit.toFixed(2)} profit/trace ({Math.round(profit / pricing.single_price * 100)}% margin)
                  </div>
                  <div className="space-y-1.5">
                    {info.features.map(f => (
                      <div key={f} className="flex items-center gap-2 text-xs text-zinc-300">
                        <i className="fa-solid fa-check text-[8px]" style={{ color: c.text }} /> {f.replace('_', ' ')}
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
